import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { auth } from '@/lib/firebase/config';
import { signOut as firebaseSignOut, signInWithEmailAndPassword } from 'firebase/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc, onSnapshot, updateDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { UserRoles } from '@/lib/firebase/auth';
import { posthog } from '@/lib/analytics/posthog';
import { syncPublicProfile } from '@/lib/firebase/user/publicProfile';

interface AuthContextType {
  user: any;
  loading: boolean;
  userRoles: UserRoles | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  userRoles: null,
  signIn: async () => { },
  signOut: async () => { },
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [userRoles, setUserRoles] = useState<UserRoles | null>(null);
  const [hasSeenCreatorWelcome, setHasSeenCreatorWelcome] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();
  // Tracks which uid we've already sent to PostHog, so identify() fires once
  // per sign-in (not on every user-doc snapshot) even though roles arrive async.
  const identifiedUidRef = useRef<string | null>(null);
  // Tracks which uid we've already backfilled to publicProfiles/{uid} this
  // session (DF-190) -- the doc listener re-fires on every field change, and
  // this must not turn into a write per snapshot.
  const backfilledProfileUidRef = useRef<string | null>(null);

  useEffect(() => {
    let unsubscribeFromUserDoc: (() => void) | null = null;

    const unsubscribe = auth.onAuthStateChanged(async (authUser) => {
      if (authUser) {
        // Tell the Control Center chat widget who this is, so it never asks a
        // signed-in member for an address we already have. Deliberately NOT
        // folded into the analytics identify below: that one is gated on cookie
        // consent, and someone who declined analytics should still not be made
        // to retype their email to get a reply. Guarded because the widget is a
        // deferred script and this listener usually wins the race — it drains
        // _pending on boot.
        try {
          const cc = ((window as unknown as { ccChat?: Record<string, unknown> }).ccChat ??= {}) as {
            identify?: (w: unknown) => void;
            _pending?: unknown;
          };
          if (authUser.email) {
            const who = { email: authUser.email, name: authUser.displayName ?? undefined };
            if (cc.identify) cc.identify(who);
            else cc._pending = who;
          }
        } catch {
          /* the chat is a nicety; never let it break auth */
        }
        if (identifiedUidRef.current !== authUser.uid) {
          identifiedUidRef.current = authUser.uid;
          try {
            posthog.identify(authUser.uid);
          } catch {
            /* posthog not initialised (no token) — nothing to identify to */
          }
        }
        try {
          // Set up real-time listener for user document
          unsubscribeFromUserDoc = onSnapshot(
            doc(db, 'users', authUser.uid),
            (userDoc) => {
              if (userDoc.exists()) {
                const userData = userDoc.data();
                const roles = userData.roles as UserRoles;

                // The auth email changes out-of-band (verifyBeforeUpdateEmail
                // link); keep the doc the mailers read in step with it.
                if (authUser.email && authUser.emailVerified && userData.email !== authUser.email) {
                  updateDoc(doc(db, 'users', authUser.uid), { email: authUser.email }).catch(() => {});
                }

                // Merge Firestore data with Firebase Auth user
                const enrichedUser = {
                  ...authUser,
                  firstName: userData.firstName,
                  lastName: userData.lastName,
                  displayName: userData.displayName || authUser.displayName,
                  photoURL: userData.photoURL || authUser.photoURL,
                };

                setUser(enrichedUser);
                setUserRoles(roles);
                setHasSeenCreatorWelcome(!!userData.hasSeenCreatorWelcome);

                // Backfill publicProfiles/{uid} for creators who signed up before
                // the DF-185/DF-190 split (MyPage.tsx already does this on the
                // dashboard, but this listener covers every page of a session,
                // so creators who never visit MyPage still get mirrored). Once
                // per uid per session -- fire-and-forget, never blocks auth.
                if (roles?.creator && backfilledProfileUidRef.current !== authUser.uid) {
                  backfilledProfileUidRef.current = authUser.uid;
                  void syncPublicProfile(authUser.uid, userData);
                }

                try {
                  // Safe, non-PII traits only — no name/email.
                  posthog.setPersonProperties({
                    is_creator: !!roles?.creator,
                    is_member: !!roles?.member,
                  });
                } catch {
                  /* posthog not initialised — nothing to set */
                }

                // Only redirect on auth pages
                const isAuthPage = location.pathname === '/signin' || location.pathname === '/signup' || location.pathname === '/signup-creator';
                if (isAuthPage) {
                  // ?next=/churches/x/claim — send people back to what they were doing.
                  // Same-origin paths only, never "//host".
                  const next = new URLSearchParams(location.search).get('next');
                  if (next && next.startsWith('/') && !next.startsWith('//')) {
                    navigate(next);
                  } else if (roles.creator) {
                    navigate('/creator');
                  } else {
                    navigate('/dashboard');
                  }
                }
              } else {
                // Document doesn't exist yet (during signup), just use auth user
                setUser(authUser);
                setUserRoles(null);
              }
              setLoading(false);
            },
            (error) => {
              console.error('Error listening to user document:', error);
              // On error, still set the auth user and continue
              setUser(authUser);
              setUserRoles(null);
              setLoading(false);
            }
          );
        } catch (error) {
          console.error('Error setting up user listener:', error);
          setUser(authUser);
          setLoading(false);
        }
      } else {
        if (identifiedUidRef.current !== null) {
          identifiedUidRef.current = null;
          try {
            posthog.reset();
          } catch {
            /* posthog not initialised — nothing to reset */
          }
        }
        setUser(null);
        setUserRoles(null);
        setLoading(false);
      }
    });

    return () => {
      unsubscribe();
      if (unsubscribeFromUserDoc) {
        unsubscribeFromUserDoc();
      }
    };
  }, [navigate, location]);

  const signIn = async (email: string, password: string) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error('Error signing in:', error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, userRoles, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);